import React, { useEffect, useState } from 'react';

import CloseIcon from '@mui/icons-material/Close';
import {
  Alert,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  TextField,
  Typography,
} from '@mui/material';

import CustomSelect from 'components/form/CustomSelect';
import { MSG_MAX_LENGTH, MSG_REQUIRED } from 'constant/ValidationMessages';
import { TicketCategory, TicketScope, createTicketType } from 'stores/TicketTypeStoreAdapter';
import { successDefault, toErrorDetail } from 'utils/Toastify';

type Props = {
  open: boolean;
  onClose: () => void;
  onCreated: (typeId: string) => void;
  /** GLOBAL 種別を作れるか（ADMIN/BACK_OFFICE） */
  canCreateGlobal: boolean;
  /** 指定時はスコープを固定（設定画面ごとに GLOBAL/PRIVATE を固定） */
  fixedScope?: TicketScope;
};

const RESERVED_PREFIX = ['PRV', 'REQ', 'CONT'];

/**
 * チケット種別作成ダイアログ（D10-1-4）。
 * - スコープ: ADMIN/BACK_OFFICE のみ GLOBAL/PRIVATE 選択可・一般は PRIVATE 固定
 * - 分類の動的制御: scope=PRIVATE → TASK固定 / category=APPROVAL → scope=GLOBAL固定
 * - keyPrefix: GLOBAL時のみ・英字最大5・PRV/REQ/CONT禁止
 */
const TicketTypeCreateDialog: React.FC<Props> = ({
  open,
  onClose,
  onCreated,
  canCreateGlobal,
  fixedScope,
}) => {
  const initialScope: TicketScope = fixedScope ?? (canCreateGlobal ? 'GLOBAL' : 'PRIVATE');
  const [name, setName] = useState('');
  const [scope, setScope] = useState<TicketScope>(initialScope);
  const [category, setCategory] = useState<TicketCategory>('TASK');
  const [keyPrefix, setKeyPrefix] = useState('');
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open) return;
    setName('');
    setScope(fixedScope ?? (canCreateGlobal ? 'GLOBAL' : 'PRIVATE'));
    setCategory('TASK');
    setKeyPrefix('');
    setErrors({});
  }, [open, canCreateGlobal, fixedScope]);

  // 動的制御: scope=PRIVATE → category=TASK固定
  const handleScopeChange = (v: string) => {
    const next = v as TicketScope;
    setScope(next);
    if (next === 'PRIVATE') setCategory('TASK');
  };
  // 動的制御: category=APPROVAL → scope=GLOBAL固定
  const handleCategoryChange = (v: string) => {
    const next = v as TicketCategory;
    setCategory(next);
    if (next === 'APPROVAL') setScope('GLOBAL');
  };

  const isGlobal = scope === 'GLOBAL';

  const validate = (): boolean => {
    const e: Record<string, string> = {};
    if (!name.trim()) e.name = MSG_REQUIRED('種別名');
    else if (name.length > 200) e.name = MSG_MAX_LENGTH('種別名', 200);
    if (isGlobal) {
      if (!keyPrefix.trim()) e.keyPrefix = MSG_REQUIRED('キープレフィックス');
      else if (!/^[A-Za-z]{1,5}$/.test(keyPrefix))
        e.keyPrefix = '英字最大5文字で入力してください。';
      else if (RESERVED_PREFIX.includes(keyPrefix.toUpperCase()))
        e.keyPrefix = 'PRV / REQ / CONT は使用できません。';
    }
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleCreate = () => {
    if (!validate() || submitting) return;
    setSubmitting(true);
    createTicketType({
      name: name.trim(),
      scope,
      ticketCategory: category,
      keyPrefix: isGlobal ? keyPrefix.trim() : undefined,
    })
      .then((typeId) => {
        successDefault('種別を作成しました');
        onCreated(typeId);
        onClose();
      })
      .catch((err) => setErrors({ submit: toErrorDetail(err) ?? '作成に失敗しました' }))
      .finally(() => setSubmitting(false));
  };

  const scopeOptions = [
    { value: 'GLOBAL', label: 'グローバル' },
    { value: 'PRIVATE', label: 'プライベート' },
  ];
  const categoryOptions = [
    { value: 'TASK', label: 'タスク' },
    { value: 'APPROVAL', label: '承認' },
  ];

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="xs"
      PaperProps={{ elevation: 0, sx: { borderRadius: 3, border: 1, borderColor: 'divider' } }}
    >
      <DialogTitle
        sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: 1 }}
      >
        <Typography variant="subtitle1" fontWeight={600}>
          {fixedScope === 'GLOBAL'
            ? 'グローバルチケット設定の追加'
            : fixedScope === 'PRIVATE'
              ? 'プライベートチケット設定の追加'
              : '設定の追加'}
        </Typography>
        <IconButton size="small" onClick={onClose}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          <TextField
            size="small"
            fullWidth
            required
            label="名称"
            value={name}
            onChange={(e) => setName(e.target.value)}
            error={!!errors.name}
            helperText={errors.name}
            slotProps={{ htmlInput: { maxLength: 200 } }}
            sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
          />

          {/* スコープ: fixedScope 指定時は固定（設定画面ごとに GLOBAL/PRIVATE 確定）・非表示 */}
          {fixedScope ? null : canCreateGlobal ? (
            <CustomSelect
              mode="single"
              label="スコープ"
              options={scopeOptions}
              value={scope}
              onChange={handleScopeChange}
            />
          ) : (
            <TextField
              size="small"
              fullWidth
              label="スコープ"
              value="プライベート"
              disabled
              helperText="一般ユーザーはプライベート種別のみ作成できます"
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
            />
          )}

          <CustomSelect
            mode="single"
            label="分類"
            options={categoryOptions}
            value={category}
            onChange={handleCategoryChange}
            disabled={scope === 'PRIVATE'}
            helperText={
              scope === 'PRIVATE'
                ? 'プライベートはタスク固定です'
                : '承認を選ぶとグローバル固定になります'
            }
          />

          {isGlobal && (
            <TextField
              size="small"
              fullWidth
              required
              label="キープレフィックス"
              value={keyPrefix}
              onChange={(e) => setKeyPrefix(e.target.value.toUpperCase())}
              error={!!errors.keyPrefix}
              helperText={errors.keyPrefix ?? '英字最大5文字（PRV/REQ/CONT 不可）'}
              slotProps={{ htmlInput: { maxLength: 5 } }}
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
            />
          )}

          {errors.submit && <Alert severity="error">{errors.submit}</Alert>}

          <Stack direction="row" spacing={1} justifyContent="flex-end" sx={{ pt: 1 }}>
            <Button onClick={onClose} sx={{ textTransform: 'none', fontWeight: 600 }}>
              キャンセル
            </Button>
            <Button
              variant="contained"
              disabled={submitting}
              onClick={handleCreate}
              sx={{
                borderRadius: 2,
                textTransform: 'none',
                fontWeight: 600,
                boxShadow: 'none',
                '&:hover': { boxShadow: 'none' },
              }}
            >
              作成
            </Button>
          </Stack>
        </Stack>
      </DialogContent>
    </Dialog>
  );
};

export default TicketTypeCreateDialog;
